import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { MdArrowBack, MdSave, MdAdd, MdDelete, MdAttachMoney } from 'react-icons/md';
import { clientesAPI, inventarioAPI } from '../services/api';
import api from '../services/api';

const Container = styled.div`
  width: 100%;
  max-width: 100%;
`;

const PageHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${p => p.theme.spacing.lg};
  margin-bottom: ${p => p.theme.spacing.xl};
`;

const BackButton = styled.button`
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${p => p.theme.colors.backgroundLight};
  border: 1px solid ${p => p.theme.colors.border};
  border-radius: ${p => p.theme.borderRadius.md};
  color: ${p => p.theme.colors.textPrimary};
  cursor: pointer;
  transition: ${p => p.theme.transitions.fast};

  svg { font-size: 1.5rem; }

  &:hover {
    border-color: ${p => p.theme.colors.primary};
    color: ${p => p.theme.colors.primary};
  }
`;

const Title = styled.h1`
  font-size: ${p => p.theme.fontSizes.xxxl};
  color: ${p => p.theme.colors.textPrimary};
  margin: 0;
`;

const FormCard = styled.div`
  background: ${p => p.theme.colors.backgroundLight};
  border-radius: ${p => p.theme.borderRadius.lg};
  padding: ${p => p.theme.spacing.xl};
  box-shadow: ${p => p.theme.shadows.card};
  margin-bottom: ${p => p.theme.spacing.xl};
`;

const SectionTitle = styled.h2`
  font-size: ${p => p.theme.fontSizes.xl};
  color: ${p => p.theme.colors.textPrimary};
  margin: 0 0 ${p => p.theme.spacing.lg} 0;
  font-weight: ${p => p.theme.fontWeights.semibold};
  display: flex;
  align-items: center;
  gap: ${p => p.theme.spacing.sm};

  svg { color: ${p => p.theme.colors.primary}; }
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: ${p => p.theme.spacing.lg};
  margin-bottom: ${p => p.theme.spacing.xl};
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${p => p.theme.spacing.sm};
`;

const Label = styled.label`
  font-size: ${p => p.theme.fontSizes.sm};
  color: ${p => p.theme.colors.textPrimary};
  font-weight: ${p => p.theme.fontWeights.medium};

  span.required { color: ${p => p.theme.colors.danger}; }
`;

const Input = styled.input`
  padding: ${p => p.theme.spacing.md};
  border: 1px solid ${p => p.theme.colors.border};
  border-radius: ${p => p.theme.borderRadius.md};
  font-size: ${p => p.theme.fontSizes.sm};
  background-color: ${p => p.theme.colors.background};
  color: ${p => p.theme.colors.textPrimary};
  transition: ${p => p.theme.transitions.fast};

  &:focus {
    border-color: ${p => p.theme.colors.primary};
    background-color: ${p => p.theme.colors.backgroundLight};
    outline: none;
  }

  &::placeholder { color: ${p => p.theme.colors.textLight}; }
`;

const Select = styled.select`
  padding: ${p => p.theme.spacing.md};
  border: 1px solid ${p => p.theme.colors.border};
  border-radius: ${p => p.theme.borderRadius.md};
  font-size: ${p => p.theme.fontSizes.sm};
  background-color: ${p => p.theme.colors.background};
  color: ${p => p.theme.colors.textPrimary};
  cursor: pointer;

  &:focus {
    border-color: ${p => p.theme.colors.primary};
    outline: none;
  }
`;

const TextArea = styled.textarea`
  padding: ${p => p.theme.spacing.md};
  border: 1px solid ${p => p.theme.colors.border};
  border-radius: ${p => p.theme.borderRadius.md};
  font-size: ${p => p.theme.fontSizes.sm};
  background-color: ${p => p.theme.colors.background};
  color: ${p => p.theme.colors.textPrimary};
  font-family: ${p => p.theme.fonts.primary};
  min-height: 80px;
  resize: vertical;

  &:focus {
    border-color: ${p => p.theme.colors.primary};
    outline: none;
  }

  &::placeholder { color: ${p => p.theme.colors.textLight}; }
`;

/* ── Items ── */
const ItemsSection = styled.div`
  background: ${p => p.theme.colors.background};
  border-radius: ${p => p.theme.borderRadius.lg};
  padding: ${p => p.theme.spacing.lg};
  margin-bottom: ${p => p.theme.spacing.lg};
`;

const ItemsHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${p => p.theme.spacing.lg};
`;

const ItemsTitle = styled.h3`
  font-size: ${p => p.theme.fontSizes.lg};
  color: ${p => p.theme.colors.textPrimary};
  margin: 0;
  font-weight: ${p => p.theme.fontWeights.semibold};
`;

const AddItemButton = styled.button`
  padding: ${p => p.theme.spacing.sm} ${p => p.theme.spacing.md};
  background-color: ${p => p.theme.colors.primary};
  color: white;
  border: none;
  border-radius: ${p => p.theme.borderRadius.md};
  font-size: ${p => p.theme.fontSizes.sm};
  font-weight: ${p => p.theme.fontWeights.medium};
  display: flex;
  align-items: center;
  gap: ${p => p.theme.spacing.xs};
  transition: ${p => p.theme.transitions.fast};
  cursor: pointer;

  svg { font-size: 1rem; }

  &:hover {
    background-color: ${p => p.theme.colors.primaryDark};
  }
`;

const ItemRow = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1fr 40px;
  gap: ${p => p.theme.spacing.md};
  align-items: end;
  margin-bottom: ${p => p.theme.spacing.md};
  padding: ${p => p.theme.spacing.md};
  background: ${p => p.theme.colors.backgroundLight};
  border-radius: ${p => p.theme.borderRadius.md};
  border: 1px solid ${p => p.theme.colors.border};

  @media (max-width: ${p => p.theme.breakpoints.tablet}) {
    grid-template-columns: 1fr;
  }
`;

const DeleteButton = styled.button`
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${p => p.theme.colors.danger}15;
  color: ${p => p.theme.colors.danger};
  border: 1px solid ${p => p.theme.colors.danger}30;
  border-radius: ${p => p.theme.borderRadius.md};
  transition: ${p => p.theme.transitions.fast};
  cursor: pointer;

  svg { font-size: 1.25rem; }

  &:hover {
    background-color: ${p => p.theme.colors.danger};
    color: white;
  }
`;

const TotalRow = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: ${p => p.theme.spacing.lg};
  padding: ${p => p.theme.spacing.md} 0;
  border-top: 2px solid ${p => p.theme.colors.border};
  margin-top: ${p => p.theme.spacing.md};
`;

const TotalLabel = styled.span`
  font-size: ${p => p.theme.fontSizes.md};
  color: ${p => p.theme.colors.textSecondary};
  font-weight: 600;
`;

const TotalValue = styled.span`
  font-size: ${p => p.theme.fontSizes.xxl};
  color: ${p => p.theme.colors.textPrimary};
  font-weight: 700;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: ${p => p.theme.spacing.md};
  justify-content: flex-end;
  margin-top: ${p => p.theme.spacing.xl};
  padding-top: ${p => p.theme.spacing.xl};
  border-top: 1px solid ${p => p.theme.colors.border};
`;

const Btn = styled.button`
  padding: ${p => p.theme.spacing.md} ${p => p.theme.spacing.xl};
  background-color: ${p => p.primary ? p.theme.colors.primary : p.theme.colors.backgroundLight};
  color: ${p => p.primary ? 'white' : p.theme.colors.textPrimary};
  border: 1px solid ${p => p.primary ? p.theme.colors.primary : p.theme.colors.border};
  border-radius: ${p => p.theme.borderRadius.md};
  font-size: ${p => p.theme.fontSizes.sm};
  font-weight: ${p => p.theme.fontWeights.semibold};
  display: flex;
  align-items: center;
  gap: ${p => p.theme.spacing.sm};
  cursor: pointer;
  transition: ${p => p.theme.transitions.fast};

  svg { font-size: 1.25rem; }

  &:hover {
    background-color: ${p => p.primary ? p.theme.colors.primaryDark : p.theme.colors.background};
    transform: translateY(-1px);
  }

  &:disabled { opacity: 0.6; cursor: not-allowed; transform: none; }
`;

const ErrorMsg = styled.p`
  color: ${p => p.theme.colors.danger};
  font-size: ${p => p.theme.fontSizes.sm};
  margin: ${p => p.theme.spacing.md} 0 0;
  padding: ${p => p.theme.spacing.md};
  background: ${p => p.theme.colors.danger}10;
  border-radius: ${p => p.theme.borderRadius.md};
  border-left: 3px solid ${p => p.theme.colors.danger};
`;

const todayStr = () => new Date().toISOString().split('T')[0];
const plus30 = () => {
  const d = new Date();
  d.setDate(d.getDate() + 30);
  return d.toISOString().split('T')[0];
};

const emptyItem = () => ({ id: Date.now(), descripcion: '', cantidad: 1, precioUnitario: 0, total: 0 });

const NewQuote = () => {
  const navigate = useNavigate();
  const [clientes, setClientes] = useState([]);
  const [invItems, setInvItems] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [form, setForm] = useState({
    cliente_id: '',
    fecha_emision: todayStr(),
    fecha_validez: plus30(),
    notas: '',
  });
  const [items, setItems] = useState([emptyItem()]);

  useEffect(() => {
    clientesAPI.getAll()
      .then(res => setClientes((res.data.data || []).filter(c => !c.padre_cliente_id)))
      .catch(() => {});
    inventarioAPI.getAll()
      .then(res => setInvItems((res.data.data || []).filter(i => i.activo)))
      .catch(() => {});
  }, []);

  const handleChange = e => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleItemChange = (id, field, value) => {
    setItems(prev => prev.map(item => {
      if (item.id !== id) return item;
      const updated = { ...item, [field]: value };
      if (field === 'cantidad' || field === 'precioUnitario') {
        updated.total = parseFloat(updated.cantidad || 0) * parseFloat(updated.precioUnitario || 0);
      }
      return updated;
    }));
  };

  const handleCatalogSelect = (itemId, invItemId) => {
    if (!invItemId) return;
    const inv = invItems.find(i => String(i.id) === String(invItemId));
    if (!inv) return;
    setItems(prev => prev.map(item => {
      if (item.id !== itemId) return item;
      const precio = parseFloat(inv.precio) || 0;
      const cantidad = parseFloat(item.cantidad) || 1;
      return { ...item, descripcion: inv.nombre, precioUnitario: precio, total: precio * cantidad };
    }));
  };

  const addItem = () => setItems(prev => [...prev, emptyItem()]);

  const removeItem = id => {
    if (items.length === 1) return;
    setItems(prev => prev.filter(item => item.id !== id));
  };

  const total = items.reduce((sum, item) => sum + (item.total || 0), 0);

  const handleSubmit = async e => {
    e.preventDefault();
    if (!form.cliente_id) {
      setError('Selecciona un cliente');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const payload = {
        ...form,
        cliente_id: parseInt(form.cliente_id),
        items: items.map(item => ({
          descripcion: item.descripcion,
          cantidad: parseFloat(item.cantidad) || 1,
          precio_unitario: parseFloat(item.precioUnitario) || 0,
        })),
      };
      const res = await api.post('/cotizaciones', payload);
      const newId = res.data.data?.id || res.data.id;
      navigate(`/cotizaciones/${newId}`);
    } catch (err) {
      setError(err.message || 'Error al crear la cotizacion');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Container>
      <PageHeader>
        <BackButton onClick={() => navigate(-1)}>
          <MdArrowBack />
        </BackButton>
        <Title>Nueva Cotizacion</Title>
      </PageHeader>

      <form onSubmit={handleSubmit}>
        <FormCard>
          <SectionTitle>
            <MdAttachMoney />
            Datos de la Cotizacion
          </SectionTitle>
          <FormGrid>
            <FormGroup>
              <Label>Cliente <span className="required">*</span></Label>
              <Select name="cliente_id" value={form.cliente_id} onChange={handleChange} required>
                <option value="">Seleccionar cliente...</option>
                {clientes.map(c => (
                  <option key={c.id} value={c.id}>{c.nombre}</option>
                ))}
              </Select>
            </FormGroup>
            <FormGroup>
              <Label>Fecha de Emision</Label>
              <Input
                type="date"
                name="fecha_emision"
                value={form.fecha_emision}
                onChange={handleChange}
              />
            </FormGroup>
            <FormGroup>
              <Label>Valida Hasta</Label>
              <Input
                type="date"
                name="fecha_validez"
                value={form.fecha_validez}
                onChange={handleChange}
              />
            </FormGroup>
            <FormGroup style={{ gridColumn: '1 / -1' }}>
              <Label>Notas</Label>
              <TextArea
                name="notas"
                value={form.notas}
                onChange={handleChange}
                placeholder="Condiciones, observaciones o notas adicionales..."
              />
            </FormGroup>
          </FormGrid>
        </FormCard>

        <FormCard>
          <SectionTitle>
            <MdAdd />
            Items de la Cotizacion
          </SectionTitle>

          <ItemsSection>
            <ItemsHeader>
              <ItemsTitle>Conceptos / Servicios</ItemsTitle>
              <AddItemButton type="button" onClick={addItem}>
                <MdAdd />
                Agregar Item
              </AddItemButton>
            </ItemsHeader>

            {items.map((item) => (
              <ItemRow key={item.id}>
                <FormGroup>
                  <Label>Descripción <span className="required">*</span></Label>
                  {invItems.length > 0 && (
                    <Select
                      style={{ marginBottom: 6 }}
                      value=""
                      onChange={e => handleCatalogSelect(item.id, e.target.value)}
                    >
                      <option value="">— Del catálogo —</option>
                      {invItems.map(i => (
                        <option key={i.id} value={i.id}>{i.nombre}</option>
                      ))}
                    </Select>
                  )}
                  <Input
                    type="text"
                    value={item.descripcion}
                    onChange={e => handleItemChange(item.id, 'descripcion', e.target.value)}
                    placeholder="Ej: Servicio de consultoría"
                    required
                  />
                </FormGroup>

                <FormGroup>
                  <Label>Cantidad</Label>
                  <Input
                    type="number"
                    value={item.cantidad}
                    onChange={e => handleItemChange(item.id, 'cantidad', e.target.value)}
                    min="0.01"
                    step="0.01"
                  />
                </FormGroup>

                <FormGroup>
                  <Label>Precio Unit. ($)</Label>
                  <Input
                    type="number"
                    value={item.precioUnitario}
                    onChange={e => handleItemChange(item.id, 'precioUnitario', e.target.value)}
                    min="0"
                    step="0.01"
                  />
                </FormGroup>

                <FormGroup>
                  <Label>Total ($)</Label>
                  <Input
                    type="number"
                    value={(item.total || 0).toFixed(2)}
                    readOnly
                    style={{ backgroundColor: '#f5f7fa', cursor: 'not-allowed' }}
                  />
                </FormGroup>

                <DeleteButton
                  type="button"
                  onClick={() => removeItem(item.id)}
                  disabled={items.length === 1}
                  style={{ opacity: items.length === 1 ? 0.5 : 1 }}
                >
                  <MdDelete />
                </DeleteButton>
              </ItemRow>
            ))}
          </ItemsSection>

          <TotalRow>
            <TotalLabel>Total:</TotalLabel>
            <TotalValue>
              ${total.toLocaleString('es-MX', { minimumFractionDigits: 2 })}
            </TotalValue>
          </TotalRow>

          {error && <ErrorMsg>{error}</ErrorMsg>}

          <ButtonGroup>
            <Btn type="button" onClick={() => navigate(-1)} disabled={submitting}>
              Cancelar
            </Btn>
            <Btn type="submit" primary disabled={submitting}>
              <MdSave />
              {submitting ? 'Creando...' : 'Crear Cotizacion'}
            </Btn>
          </ButtonGroup>
        </FormCard>
      </form>
    </Container>
  );
};

export default NewQuote;
